import type { Theme } from '@mui/system'

import type { Budget, BudgetColors, BudgetsCopy, DetailedCategory, DetailedBudget } from '../types'

import { findDetailedCategory, findDetailedSubCategory } from './CategoryUtil'
import { formatCurrency } from './NumberFormatting'
import { buildDynamicCopy } from './Localization'

export const isInvalidAmount = (val: string | undefined) =>
  val === undefined || val === '' || Number(val) < 0 || isNaN(Number(val))

export const getBudgetPercentage = (budgetAmount: number, totalAmount: number) => {
  // No budget amount and nothing spent results in zero percent and green bubble
  if (!budgetAmount && !totalAmount) {
    return 0
  }

  // No budget amount and money spent results in 101 percent and red bubble
  if (!budgetAmount && totalAmount) {
    return 101
  }

  // Budget amount and money spent results in calculating percentage for color
  return (totalAmount / budgetAmount) * 100
}

export const getBudgetColors = (percentage: number, theme: Theme) => {
  const budgetColors: BudgetColors = {
    background: theme.palette.success.main,
    description: percentage <= 100 ? theme.palette.text.secondary : theme.palette.error.main,
    text: theme.palette.common.white,
    mercury: theme.palette.success.lighter,
  }

  if (percentage > 100) {
    budgetColors.background = theme.palette.error.main
    budgetColors.mercury = theme.palette.error.lighter
  } else if (percentage > 80) {
    budgetColors.background = theme.palette.warning.main
    budgetColors.text = theme.palette.grey['900']
    budgetColors.mercury = theme.palette.warning.lighter
  }

  return budgetColors
}

export const getBudgetDifference = (budgets: DetailedBudget[]) => {
  const differences = budgets.map((budget) => {
    const currentAmount = budget.transaction_total
    const totalAmount = budget.amount
    return totalAmount - currentAmount
  })

  return differences.reduce((total, difference) => total + difference, 0)
}

export const augmentBudgets = (
  budgets: Budget[],
  categories: DetailedCategory[],
  copy: BudgetsCopy,
  theme: Theme,
): DetailedBudget[] => {
  const detailedBudgets = [] as DetailedBudget[]

  const childTotals = new Map<string, number>()
  for (const b of budgets) {
    if (b.parent_guid) {
      childTotals.set(b.parent_guid, (childTotals.get(b.parent_guid) ?? 0) + b.amount)
    }
  }

  // Top-level Categories
  for (const budget of budgets) {
    const category = findDetailedCategory(categories, budget.category_guid)
    if (!category) continue

    const amount = childTotals.get(budget.guid) ?? budget.amount
    const spent = category.totalAmount
    const percentage = getBudgetPercentage(amount, spent)
    const difference = amount - spent

    detailedBudgets.push({
      ...budget,
      amount,
      budgetColors: getBudgetColors(percentage, theme),
      category,
      description: buildDynamicCopy(
        difference < 0 ? copy.budget_remaining_over : copy.budget_remaining_left,
        formatCurrency(Math.abs(difference), '0,0'),
      ) as string,
      percentage,
      subBudgets: [],
      transaction_total: spent,
    })
  }

  // Sub categories
  for (const budget of budgets) {
    if (!budget.parent_guid) continue

    const category = findDetailedSubCategory(categories, budget.category_guid)
    if (!category) continue

    const parentBudget = detailedBudgets.find((b) => b.guid === budget.parent_guid)
    if (!parentBudget) continue

    const spent = category.currentAmount
    const difference = budget.amount - spent
    const percentage = getBudgetPercentage(budget.amount, spent)

    parentBudget.subBudgets.push({
      ...budget,
      budgetColors: getBudgetColors(percentage, theme),
      category,
      description: buildDynamicCopy(
        difference < 0 ? copy.budget_remaining_over : copy.budget_remaining_left,
        formatCurrency(Math.abs(difference), '0,0'),
      ) as string,
      percentage,
      subBudgets: [],
      transaction_total: spent,
    })
  }

  return detailedBudgets
}

export const findBudget = (budgets: DetailedBudget[], guid: string): DetailedBudget | undefined => {
  return budgets.find((budget) => budget.guid === guid)
}

export const findSubBudget = (
  budgets: DetailedBudget[],
  guid: string,
  parentGuid: string,
): DetailedBudget | undefined => {
  const parent = findBudget(budgets, parentGuid)
  if (!parent) return undefined

  return findBudget(parent.subBudgets, guid)
}

export const getRemainingAmountSpentPercentage = (totalTransaction: number, amount: number) => {
  const remainingAmount = Math.max(0, amount - totalTransaction)
  const spentPercentage = amount > 0 ? Math.min(100, (totalTransaction / amount) * 100) : 0

  let iconColor = 'success'
  if (spentPercentage >= 100) iconColor = 'error'
  else if (spentPercentage >= 50) iconColor = 'warning'

  return {
    remainingAmount,
    spentPercentage,
    iconColor,
  }
}
